import { ChangeEvent, useState } from "react";

import { useOnce } from "src/hooks/lifecycle";
import i18n from "src/locales";
import { calculateAge } from "src/utils/datetime";
import { formatDobInput, validateAndParseDob } from "src/utils/dobUtils";

export type UseDateOfBirthOptions = {
  /** Current display value (`mm/dd/yyyy`, partial or complete). */
  value: string;
  /**
   * Called on every keystroke with slash-formatted input.
   * Use to update the caller's source of truth (e.g. enrollment Redux).
   */
  onChange: (formatted: string) => void;
  /**
   * Called only when input is a complete, valid calendar date.
   * Use for secondary writes (e.g. surveyAnswers sync, age branching).
   */
  onSuccessfulValidation?: (date: Date, age: number) => void;
  /**
   * Runs once on mount. Resolve an external value and call `onChange` to seed
   * the caller's source of truth (getPrefillValue + onChange).
   */
  prefill?: () => void;
};

export type UseDateOfBirthReturn = {
  displayValue: string;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  parsedDate: Date | null;
  age: number | null;
  canProceed: boolean;
  errorMessage: string | null;
  showBlurErrors: boolean;
};

/**
 * Controlled DOB input: formatting, intrinsic validation, blur errors.
 * Storage and prefill are the caller's responsibility (`value` / `onChange`).
 */
export function useDateOfBirth({
  value,
  onChange,
  onSuccessfulValidation,
  prefill,
}: UseDateOfBirthOptions): UseDateOfBirthReturn {
  useOnce(() => {
    prefill?.();
  });

  const [showIncompleteError, setShowIncompleteError] = useState(false);

  const { parsedDate, errorKey } = validateAndParseDob(value, {
    showIncompleteError,
  });
  const errorMessage = errorKey ? i18n.t(errorKey) : null;
  const age = parsedDate ? calculateAge(parsedDate) : null;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDobInput(event.target.value);
    setShowIncompleteError(false);
    onChange(formatted);

    const { parsedDate: parsed } = validateAndParseDob(formatted);
    if (parsed) {
      onSuccessfulValidation?.(parsed, calculateAge(parsed));
    }
  };

  const handleBlur = () => {
    setShowIncompleteError(true);
  };

  return {
    displayValue: value,
    handleChange,
    handleBlur,
    parsedDate,
    age,
    canProceed: parsedDate !== null,
    errorMessage,
    showBlurErrors: showIncompleteError,
  };
}
